import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import { AppHeader } from "@/components/AppHeader";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const TermsConditions = () => {
  const { t } = useTranslation();

  const sections = [
    {
      key: "scope",
      hasProvider: true,
    },
    {
      key: "description",
      hasList: true,
      listKey: "items",
      listItems: ["testData", "nestedLists", "apiAccess", "collaboration"],
    },
    {
      key: "contractFormation",
    },
    {
      key: "userAccounts",
      subsections: [
        {
          key: "creation",
          hasList: true,
          listKey: "items",
          listItems: ["accurate", "security", "update", "responsibility", "notify"],
        },
        { key: "eligibility" },
      ],
    },
    {
      key: "acceptableUse",
      hasList: true,
      listKey: "items",
      listItems: ["illegal", "laws", "malware", "unauthorized", "interfere", "collect", "spam", "impersonate", "infringe", "reverse"],
    },
    {
      key: "content",
      subsections: [
        { key: "yours" },
        { key: "ours" },
      ],
    },
    {
      key: "api",
      hasList: true,
      listKey: "items",
      listItems: ["rateLimits", "security", "legitimate", "resell"],
    },
    {
      key: "payment",
      subsections: [
        { key: "subscription" },
        { key: "billing" },
        { key: "refunds" },
      ],
    },
    {
      key: "availability",
    },
    {
      key: "dataBackup",
    },
    {
      key: "withdrawal",
    },
    {
      key: "liability",
      subsections: [
        { key: "unlimited" },
        { key: "limited" },
        { key: "exclusion" },
      ],
    },
    {
      key: "indemnification",
    },
    {
      key: "termination",
      subsections: [
        { key: "byYou" },
        { key: "byUs" },
        { key: "consequences" },
      ],
    },
    {
      key: "dataProtection",
      hasPrivacyLink: true,
    },
    {
      key: "governingLaw",
    },
    {
      key: "disputeResolution",
    },
    {
      key: "severability",
    },
    {
      key: "changes",
    },
    {
      key: "contact",
      hasEmail: true,
    },
  ];

  const { user } = useAuth();

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="min-h-screen bg-background flex flex-col w-full">
        <SEOHead
          title="Terms & Conditions – TestAssets"
          description="TestAssets terms and conditions. Read our service agreement covering usage, data, and liability."
          path="/terms"
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Terms & Conditions", url: "/terms" },
          ]}
        />
        {user ? <AppHeader /> : <LandingNav />}

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-8 prose prose-slate dark:prose-invert">
          <div className="text-center space-y-4 not-prose">
            <h1 className="text-4xl font-bold text-foreground">{t("terms.title")}</h1>
            <p className="text-muted-foreground">{t("terms.lastUpdated")}</p>
          </div>

          {sections.map((section, idx) => (
            <section key={section.key} className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                {t(`terms.${section.key}.title`)}
              </h2>

              {/* Main description */}
              {section.key === "description" ? (
                <p className="text-muted-foreground">{t(`terms.${section.key}.intro`)}</p>
              ) : section.hasProvider ? (
                <>
                  <p className="text-muted-foreground">{t(`terms.${section.key}.description`)}</p>
                  <div className="bg-muted p-4 rounded-lg space-y-1">
                    <p className="text-sm font-medium text-foreground">{t("terms.scope.provider")}</p>
                    <p className="text-sm text-muted-foreground">Christoph Schwenk</p>
                    <p className="text-sm text-muted-foreground">c/o flexdienst – #12272</p>
                    <p className="text-sm text-muted-foreground">Kurt-Schumacher-Straße 76</p>
                    <p className="text-sm text-muted-foreground">67663 Kaiserslautern, Deutschland</p>
                  </div>
                </>
              ) : section.hasPrivacyLink ? (
                <p className="text-muted-foreground">
                  {t(`terms.${section.key}.description`)}{" "}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    {t(`terms.${section.key}.link`)}
                  </Link>
                </p>
              ) : section.hasEmail ? (
                <>
                  <p className="text-muted-foreground">{t(`terms.${section.key}.description`)}</p>
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t("terms.contact.email")}:{" "}
                      <a href="mailto:support@testassets.io" className="text-primary hover:underline">
                        support@testassets.io
                      </a>
                    </p>
                  </div>
                </>
              ) : !section.subsections && !section.hasList ? (
                <p className="text-muted-foreground">{t(`terms.${section.key}.description`)}</p>
              ) : null}

              {/* List items at section level */}
              {section.hasList && (
                <>
                  {section.key !== "description" && (
                    <p className="text-muted-foreground">{t(`terms.${section.key}.description`)}</p>
                  )}
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    {section.listItems!.map((item) => (
                      <li key={item}>{t(`terms.${section.key}.${section.listKey}.${item}`)}</li>
                    ))}
                  </ul>
                </>
              )}

              {/* Subsections */}
              {section.subsections?.map((sub) => (
                <div key={sub.key} className="space-y-3">
                  <h3 className="text-xl font-medium text-foreground mt-6">
                    {t(`terms.${section.key}.${sub.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground">
                    {t(`terms.${section.key}.${sub.key}.description`)}
                  </p>
                  {sub.hasList && (
                    <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                      {sub.listItems!.map((item) => (
                        <li key={item}>
                          {t(`terms.${section.key}.${sub.key}.${sub.listKey}.${item}`)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      </main>

      <Footer />
      </div>
    </SidebarProvider>
  );
};

export default TermsConditions;
